import { ErrorInfo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { JourneyStep, PassengerInputType } from "src/Modules/Flights/constants/JourneyDetails";
import { useJourneyDynamicDetails } from "../../../../context";
import { useForm } from "antd/es/form/Form";
import { Form } from "antd";
import PassengerFormList from "../PassengersFormList/PassengersFormList";
import useStepFlow from "src/Modules/Flights/hooks/useStepFlow";
import StepButtons from "../../../../components/StepButtons/StepButtons";
import { track } from "@amplitude/analytics-browser";

export default function PassengerForm() {
  const { t } = useTranslation();
  const { passengersState } = useJourneyDynamicDetails();
  const { validateStep, goToNextStep, invalidateStep } = useStepFlow();
  const [form] = useForm();
  const activePassengerState = useState(0);
  const [activePassenger, setActivePassenger] = activePassengerState;
  const [passengers, setPassengers] = passengersState;
  const formContainerRef = useRef<HTMLDivElement>(null);

  const scrollIntoView = () => {
    formContainerRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleFinish = (values: PassengerInputType) => {
    const soleAdultIndex = passengers.passengers.filter((p) => p.type === "Adult").length === 1
      ? passengers.passengers.findIndex((p) => p.type === "Adult")
      : -1;
    setPassengers((prev) => ({
      passengers: values.passengers.map((p, i) => ({
        ...prev.passengers[i],
        basicData: {
          ...p.basicData,
          isLeadPax: soleAdultIndex >= 0 ? i === soleAdultIndex : p.basicData?.isLeadPax,
        },
      })),
    }));
    const validatity = validateStep(JourneyStep.Passengers);
    track("Passenger Data Submitted",{
      ...passengers
    })
    goToNextStep(validatity);
  };

  const handleNext = () => {
    if (activePassenger < passengers.passengers.length - 1) {
      setActivePassenger((val) => val + 1);
      scrollIntoView();
    }
  };

  const handlePrev = () => {
    setActivePassenger((val) => Math.max(val - 1, 0));
    scrollIntoView();
  };

  const handleError = (errors: ErrorInfo) => {
    const errorIndex = (errors as any).errorFields.map((error: any) => error?.name?.[1])?.[0];
    setActivePassenger(errorIndex);
    scrollIntoView();
    invalidateStep(JourneyStep.Passengers);
  };

  const formPassengers = useMemo(
    () =>
      passengers.passengers.map((p, i) => ({
        ...p,
        basicData: {
          ...p.basicData,
          isLeadPax: p.basicData?.isLeadPax ?? (p.type === "Adult" && i === passengers.passengers.findIndex((x) => x.type === "Adult")),
        },
      })),
    [passengers],
  );

  useEffect(() => {
    form.setFieldsValue({ passengers: formPassengers });
  }, [formPassengers]);

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{ passengers: formPassengers }}
      onFinish={handleFinish}
      onFinishFailed={handleError as any}
    >
      <div className="flex flex-col gap-3" ref={formContainerRef}>
        <PassengerFormList
          form={form}
          activePassengerState={activePassengerState}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      </div>
      <StepButtons
        nextLabel={t("Flights.details.steps.proceedToExtras")}
        onNext={() => form.submit()}
      />
    </Form>
  );
}
